import { useEffect, useState } from "react";
import { useMap } from "react-leaflet";
import axios from "axios";
import { geoServer, geoServerDefaultParams } from "../services/geoServer";

interface GeoJSONFeatureCollection {
  type: "FeatureCollection";
  features: any[];
  [key: string]: any;
}

export default function useGeoServerFetch(
  typename: string,
  workspace: string,
  cqlFilter?: string,
  geometryField?: string,
  fetchOnce?: boolean,
  fetchKey?: number,
  // false holds the request back (e.g. while the geometry field needed for a
  // BBOX-bounded cql_filter is still being discovered).
  enabled: boolean = true,
) {
  const [geoData, setGeoData] = useState<GeoJSONFeatureCollection | null>(null);
  const [isFetching, setIsFetching] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const map = useMap();

  useEffect(() => {
    if (!enabled) return;

    const controller = new AbortController();
    const { signal } = controller;

    setIsFetching(true);
    setError(null);

    const params: Record<string, any> = {
      ...geoServerDefaultParams,
      typename: `${workspace}:${typename}`,
      srsName: "EPSG:4326",
    };

    if (fetchOnce) {
      if (cqlFilter) params.cql_filter = cqlFilter;
    } else {
      const bounds = map.getBounds();
      if (cqlFilter) {
        if (geometryField) {
          const bboxCql = `BBOX(${geometryField},${bounds.getWest()},${bounds.getSouth()},${bounds.getEast()},${bounds.getNorth()})`;
          params.cql_filter = `${bboxCql} AND (${cqlFilter})`;
        } else {
          params.cql_filter = cqlFilter;
        }
      } else {
        params.bbox = `${bounds.getWest()},${bounds.getSouth()},${bounds.getEast()},${bounds.getNorth()}`;
      }
    }

    geoServer
      .get("wfs", { params, signal })
      .then((response) => {
        if (!signal.aborted) {
          setGeoData(response.data);
        }
      })
      .catch((err) => {
        if (axios.isCancel(err)) return;
        setError(err);
      })
      .finally(() => {
        if (!signal.aborted) {
          setIsFetching(false);
        }
      });

    return () => {
      controller.abort();
    };
  // map é uma instância estável do Leaflet — não precisa entrar nas deps
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [typename, workspace, cqlFilter, geometryField, fetchOnce, fetchKey, enabled]);

  return { geoData, error, isFetching };
}
